import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  FISCAL_ROUTES,
  apiMessage,
  type CreateFiscalProfileBody,
  type FiscalProfile,
  type FiscalRegimeDescription,
} from '../fiscalApi';

interface Props {
  target: FiscalProfile | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY: CreateFiscalProfileBody = {
  regime: 'EG_ETA',
  taxpayerId: '',
  legalName: '',
  tradeName: '',
  activityCode: '',
  branchCode: '',
  branchId: '',
};

/**
 * `POST /fiscal/profiles` / `PUT /fiscal/profiles/:id` — the taxpayer
 * identity a venue reports as. Regime-specific settings are entered as JSON
 * rather than a generated form: `GET /fiscal/regimes` lists the keys the
 * chosen regime expects so the operator is not guessing, but the shape is
 * genuinely per-regime (ADR-0005 §4 — that is the whole point of keeping it
 * in a JSON column rather than a column per regulator), and a second field
 * per settings key would be a form this dialog would have to regenerate for
 * every regime this registry ever grows.
 */
export default function ProfileFormDialog({ target, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateFiscalProfileBody>(EMPTY);
  const [settingsText, setSettingsText] = useState('{}');
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const regimes = useApi<FiscalRegimeDescription[]>(target ? FISCAL_ROUTES.regimes : null);
  const editing = target && target !== 'new' ? target : null;

  useEffect(() => {
    if (!target) return;
    if (target === 'new') {
      setForm(EMPTY);
      setSettingsText('{}');
    } else {
      setForm({
        regime: target.regime,
        branchId: target.branchId ?? '',
        taxpayerId: target.taxpayerId,
        legalName: target.legalName,
        tradeName: target.tradeName ?? '',
        activityCode: target.activityCode ?? '',
        branchCode: target.branchCode ?? '',
      });
      setSettingsText(JSON.stringify(target.regimeSettings ?? {}, null, 2));
    }
    setSettingsError(null);
  }, [target]);

  if (!target) return null;

  const set = <K extends keyof CreateFiscalProfileBody>(key: K, value: CreateFiscalProfileBody[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    let regimeSettings: Record<string, unknown>;
    try {
      regimeSettings = settingsText.trim() ? JSON.parse(settingsText) : {};
    } catch {
      setSettingsError(t('fiscal.profileForm.settingsInvalidJson'));
      return;
    }
    setSettingsError(null);
    setBusy(true);
    try {
      const common = {
        taxpayerId: form.taxpayerId,
        legalName: form.legalName,
        tradeName: form.tradeName?.trim() || undefined,
        activityCode: form.activityCode?.trim() || undefined,
        branchCode: form.branchCode?.trim() || undefined,
        regimeSettings,
      };
      if (editing) {
        await api.put(FISCAL_ROUTES.profile(editing.id), common);
      } else {
        await api.post(FISCAL_ROUTES.profiles, {
          ...common,
          regime: form.regime,
          branchId: form.branchId?.trim() || undefined,
        });
      }
      toast.success(t('fiscal.profileForm.saved'));
      onSaved();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const regime = regimes.data?.find((r) => r.regime === form.regime);

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? t('fiscal.profileForm.editTitle') : t('fiscal.profileForm.newTitle')}
      className="max-w-xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={busy || !form.taxpayerId.trim() || !form.legalName.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('fiscal.profileForm.regime')} controlId="fiscal-profile-regime">
          <Select
            value={form.regime}
            onChange={(e) => set('regime', e.target.value as CreateFiscalProfileBody['regime'])}
            disabled={!!editing}
          >
            {(regimes.data ?? [{ regime: form.regime, displayName: form.regime } as FiscalRegimeDescription]).map(
              (r) => (
                <option key={r.regime} value={r.regime}>
                  {r.displayName ?? r.regime}
                </option>
              ),
            )}
          </Select>
        </Field>

        <Field
          label={t('fiscal.profileForm.branchId')}
          hint={t('fiscal.profileForm.branchIdHint')}
          controlId="fiscal-profile-branch"
        >
          <Input value={form.branchId ?? ''} onChange={(e) => set('branchId', e.target.value)} disabled={!!editing} />
        </Field>

        <Field
          label={regime?.taxpayerIdLabel || t('fiscal.profileForm.taxpayerId')}
          required
          controlId="fiscal-profile-taxpayer"
        >
          <Input value={form.taxpayerId} onChange={(e) => set('taxpayerId', e.target.value)} />
        </Field>

        <Field label={t('fiscal.profileForm.legalName')} required controlId="fiscal-profile-legal-name">
          <Input value={form.legalName} onChange={(e) => set('legalName', e.target.value)} />
        </Field>

        <Field label={t('fiscal.profileForm.tradeName')} controlId="fiscal-profile-trade-name">
          <Input value={form.tradeName ?? ''} onChange={(e) => set('tradeName', e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('fiscal.profileForm.activityCode')} controlId="fiscal-profile-activity-code">
            <Input value={form.activityCode ?? ''} onChange={(e) => set('activityCode', e.target.value)} />
          </Field>
          <Field label={t('fiscal.profileForm.branchCode')} controlId="fiscal-profile-branch-code">
            <Input value={form.branchCode ?? ''} onChange={(e) => set('branchCode', e.target.value)} />
          </Field>
        </div>

        <Field
          label={t('fiscal.profileForm.regimeSettings')}
          error={settingsError ?? undefined}
          hint={
            regime?.settingsKeys?.length
              ? t('fiscal.profileForm.regimeSettingsHint', {
                  keys: regime.settingsKeys.map((k) => (k.required ? `${k.key}*` : k.key)).join(', '),
                })
              : undefined
          }
          controlId="fiscal-profile-settings"
        >
          <Textarea
            rows={5}
            className="font-mono text-xs"
            value={settingsText}
            onChange={(e) => setSettingsText(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
